import { Injectable, Injector } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, timeout } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { AuthAuditService } from '../services/auth-audit.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  // Emite o novo access token após o refresh, ou vazio string como sentinela de erro
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private injector: Injector) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Pular autenticação para endpoints de auth
    const apiBase = environment.API_BASE_URL;
    if (req.url.includes(`${apiBase}/auth`) || req.url.includes('/api/v1/auth')) return next.handle(req);

    const auth = this.injector.get(AuthService);
    const token = auth.getAccessToken();
    let cloned = req;
    if (token) {
      console.debug('[AuthInterceptor] Attaching access token to request', req.url);
      cloned = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    return next.handle(cloned).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          console.debug('[AuthInterceptor] 401 detected for', req.url);
          return this.handle401(req, next);
        }
        if (err instanceof HttpErrorResponse && err.status === 403) {
          console.warn('[AuthInterceptor] 403 Forbidden - user lacks permission');
          const audit = this.injector.get(AuthAuditService);
          audit.record('forbidden', { details: { url: req.url } });
          auth.logout('permission revoked');
          return throwError(() => err);
        }
        return throwError(() => err);
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const auth = this.injector.get(AuthService);

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);
      console.debug('[AuthInterceptor] Initiating token refresh via AuthService');

      // Delegar ao AuthService.refresh() — fonte única de verdade para normalização
      // de tokens e usuário (incluindo filtro de roles para o enum AuthRole)
      return auth.refresh().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          const newToken = auth.getAccessToken()!;
          this.refreshSubject.next(newToken);
          console.debug('[AuthInterceptor] Refresh completed, retrying original request');
          const retried = req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
          return next.handle(retried);
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshSubject.next(null);
          console.error('[AuthInterceptor] Refresh failed, logging out', err);
          // logout já é chamado internamente pelo AuthService.refresh() em caso de erro
          return throwError(() => err);
        })
      );
    }

    // Aguardar o refresh em progresso e repetir a requisição com o novo token
    return this.refreshSubject.pipe(
      filter(token => token !== null),
      take(1),
      timeout(20000),
      switchMap(token => {
        console.debug('[AuthInterceptor] Using refreshed token to retry request');
        const retried = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
        return next.handle(retried);
      }),
      catchError(err => {
        console.error('[AuthInterceptor] Failed to get refreshed token', err);
        auth.logout('refresh token wait timeout');
        return throwError(() => err);
      })
    );
  }
}
